import { MixMatchComponent } from "./mix-and-match-component";

interface DrawerSettings {
  drawer_mode?: boolean;
}

interface BundleLineItem {
  variantId: string | number;
  quantity: number;
  category: string;
  type: string;
  price?: number;
  properties: {
    _handle: string;
    [key: string]: any;
  };
}

interface CartFormData {
  items: Array<{
    id: string | number;
    quantity: number;
    properties: {
      [key: string]: string | number | boolean;
    };
  }>;
}

interface TierInfo {
  requiredQuantity: number;
  remainingQuantity: number;
  futureDiscount: number;
  nextTier: any;
  giftUnlockDistance: number;
  isSingle: boolean;
  isSingleRemaining: boolean;
  isNone: boolean;
  closeToGift: boolean;
}

interface MessageElements {
  singleTextEl: Element | null;
  multipleTextEl: Element | null;
  singleAddAndGetTextEl: Element | null;
  multipleAddAndGetTextEl: Element | null;
}

interface ProgressElements {
  discountItems: NodeListOf<Element>;
  giftItems: NodeListOf<Element>;
}

declare global {
  function update_cart(callback?: () => void): void;
}

export class MMCartSummary extends MixMatchComponent {
  totalQuantityEls: NodeListOf<Element>;
  totalPriceEl: Element | null;
  totalPriceWithoutDiscountEl: Element | null;
  progressEls: NodeListOf<Element>;
  giftCheckboxesEls: NodeListOf<Element>;
  addToCartButtonEl: HTMLElement | null;
  validateBundleBtn: HTMLElement | null;
  bundleDrawer: HTMLElement | null;
  drawerOverlay: HTMLElement | null;
  drawerCloseBtn: HTMLElement | null;
  drawerContinueBtn: HTMLElement | null;

  constructor() {
    super();

    // Visual elements
    this.totalQuantityEls = this.querySelectorAll(".js-total-qnt");
    this.totalPriceEl = this.querySelector(".js-total-price");
    this.totalPriceWithoutDiscountEl = this.querySelector(".js-total-price-without-discount");
    this.progressEls = this.querySelectorAll(".js-bundle-progress");
    this.giftCheckboxesEls = this.querySelectorAll("[name^='addon_gift']");
    this.addToCartButtonEl = this.querySelector(".js-add-mix-and-match") as HTMLElement | null;

    const state = window.mixMatchStore.state;

    // Check if drawer mode is enabled
    const settings: DrawerSettings = this.dataset.settings ? JSON.parse(this.dataset.settings) : {};

    // Drawer elements - only try to find them if drawer mode is enabled
    if (settings.drawer_mode) {
      this.validateBundleBtn = this.querySelector(".js-validate-bundle") as HTMLElement | null;
      this.bundleDrawer = this.querySelector(".js-bundle-drawer") as HTMLElement | null;
      this.drawerOverlay = this.querySelector(".js-drawer-overlay") as HTMLElement | null;
      this.drawerCloseBtn = this.querySelector(".js-close-drawer") as HTMLElement | null;
      this.drawerContinueBtn = this.querySelector(".js-continue-drawer") as HTMLElement | null;
    } else {
      console.warn('Drawer mode disabled, skipping drawer element selection');
      this.validateBundleBtn = null;
      this.bundleDrawer = null;
      this.drawerOverlay = null;
      this.drawerCloseBtn = null;
      this.drawerContinueBtn = null;
    }

    this._init();
    this._addProductMiniCards(state);
    this._updateGiftUI(state);
    this._updateTotalQuantity(state);
    this._updateTotalPrices(state);
    this._updateButtonsState(state);
    this._updateProgressBar(state);
    this._updateMessages(state);
    this._updateBottomCards();

    this._setGiftState(state);
    this._initGiftStateAndListener(state);
    this._showStickyOnIntersecting();
    this._updateGoalImages(state);
    this._initDrawer();
  }

  _init(): void {
    if (this.addToCartButtonEl) {
      this.addToCartButtonEl.addEventListener("click", async () => {
        await this.addToCart();
      });
    }

    // Event delegation for engraving info triggers (dynamically added elements)
    // Skip if trigger is a modal-trigger (it handles opening the popup itself)
    this.addEventListener("click", (e) => {
      const trigger = (e.target as HTMLElement).closest(".js-engraving-info-trigger");
      if (trigger && trigger.tagName.toLowerCase() !== 'modal-trigger') {
        const variantId = (trigger as HTMLElement).dataset.variantId;
        if (variantId) {
          this._showEngravingInfo(variantId);
        }
      }
    });
  }

  _showEngravingInfo(variantId: string): void {
    const modal = document.querySelector("#engraving-info-modal") as HTMLElement | null;
    const contentEl = document.querySelector("#engraving-info-content") as HTMLElement | null;
    
    if (!modal || !contentEl) {
      console.warn("Engraving info modal not found");
      return;
    }

    // Find the variant in state
    const state = window.mixMatchStore?.state;
    if (!state) return;

    // Find the variant that has propertiesList (handles case where modal saves with empty category)
    let variant: any = null;
    for (const key of Object.keys(state.variants)) {
      if (key.endsWith(`_${variantId}`) || key === `_${variantId}`) {
        const v = state.variants[key];
        // Prefer variant with propertiesList
        if (v?.propertiesList && v.propertiesList.length > 0) {
          variant = v;
          break;
        }
        // Fall back to any matching variant
        if (!variant) {
          variant = v;
        }
      }
    }

    // Build the content
    let html = '';
    
    if (variant?.propertiesList && variant.propertiesList.length > 0) {
      const totalBracelets = variant.propertiesList.length;
      variant.propertiesList.forEach((props: Record<string, string>, index: number) => {
        const braceletNum = index + 1;
        const isLastBracelet = index === totalBracelets - 1;
        
        html += `<div>`;
        if (totalBracelets > 1) {
          html += `<p class="tw-font-medium tw-text-base tw-leading-6 tw-mb-2 tw-mt-4">Bracelet ${braceletNum}</p>`;
        }
        
        // Group properties by face number
        // Handle both formats:
        // - Old format: "face1-engraving1", "face1-fontStyle"
        // - New format: "Face 1 - Gravure 1", "Typographie face 1"
        const faceGroups: { [faceNum: string]: { engravings: { num: string; value: string }[]; font?: string } } = {};
        
        for (const [key, value] of Object.entries(props)) {
          if (!value || key.startsWith('_')) continue; // Skip internal props like _handle
          
          // Try old format first
          let engravingMatch = key.match(/face(\d+)-engraving(\d+)/i);
          let fontMatch = key.match(/face(\d+)-fontStyle/i);
          
          // Try new format: "Face 1 - Gravure 1" or "Face 1 - Gravure 2"
          if (!engravingMatch) {
            engravingMatch = key.match(/Face\s*(\d+)\s*-\s*Gravure\s*(\d+)/i);
          }
          
          // Try new format: "Typographie face 1"
          if (!fontMatch) {
            fontMatch = key.match(/Typographie\s*face\s*(\d+)/i);
          }
          
          if (engravingMatch) {
            const faceNum = engravingMatch[1];
            if (!faceGroups[faceNum]) {
              faceGroups[faceNum] = { engravings: [] };
            }
            faceGroups[faceNum].engravings.push({ num: engravingMatch[2], value });
          } else if (fontMatch) {
            const faceNum = fontMatch[1];
            if (!faceGroups[faceNum]) {
              faceGroups[faceNum] = { engravings: [] };
            }
            faceGroups[faceNum].font = value;
          }
        }
        
        // Render grouped by face
        const faceEntries = Object.entries(faceGroups);
        faceEntries.forEach(([faceNum, faceData], faceIndex) => {
          const isLastFace = faceIndex === faceEntries.length - 1;
          const borderClass = isLastFace ? '' : 'tw-border-b tw-border-solid tw-border-brightGray tw-mb-2';
          
          html += `<div class="${borderClass}">`;
          html += `<p class="tw-text-sm tw-leading-5 tw-font-medium tw-mt-2 tw-mb-1">Face ${faceNum}</p>`;
          
          // Render engravings
          for (const engraving of faceData.engravings) {
            html += `<div class="tw-flex tw-justify-between tw-text-sm tw-py-0.5 tw-pl-2">`;
            html += `<span class="tw-text-xs tw-leading-4 tw-text-auroMetalSaurus tw-font-light">Gravure ${engraving.num} :</span>`;
            html += `<span class="tw-text-xs tw-leading-4 tw-text-right">${engraving.value}</span>`;
            html += `</div>`;
          }
          
          // Render font
          if (faceData.font) {
            html += `<div class="tw-flex tw-justify-between tw-text-sm tw-py-0.5 tw-pl-2 tw-pb-2">`;
            html += `<span class="tw-text-xs tw-leading-4 tw-text-auroMetalSaurus tw-font-light">Police :</span>`;
            html += `<span class="tw-text-xs tw-leading-4 tw-text-right">${faceData.font}</span>`;
            html += `</div>`;
          }
          
          html += `</div>`;
        });
        
        html += `</div>`;
        
        // Add divider between bracelets (not after last one)
        if (!isLastBracelet) {
          html += `<hr class="tw-border-t tw-border-solid tw-border-brightGray tw-my-4" />`;
        }
      });
    } else {
      html = `<p class="tw-text-sm tw-text-auroMetalSaurus">Aucune personnalisation ajoutée</p>`;
    }

    contentEl.innerHTML = html;

    // Open the modal
    modal.setAttribute("aria-expanded", "true");
  }

  _initDrawer(): void {
    // Initialize drawer functionality if validate bundle button exists
    if (this.validateBundleBtn) {
      this.validateBundleBtn.addEventListener("click", () => {
        this._openDrawer();
      });
    } else {
      console.warn('Validate bundle button not found');
    }

    if (this.drawerOverlay) {
      this.drawerOverlay.addEventListener("click", () => {
        this._closeDrawer();
      });
    }

    if (this.drawerCloseBtn) {
      this.drawerCloseBtn.addEventListener("click", () => {
        this._closeDrawer();
      });
    }

    if (this.drawerContinueBtn) {
      this.drawerContinueBtn.addEventListener("click", () => {
        this._closeDrawer();
      });
    }

    // Handle drawer add to cart button
    const drawerAddBtn = this.querySelector(".js-drawer-add-to-cart") as HTMLElement | null;
    if (drawerAddBtn) {
      drawerAddBtn.addEventListener("click", async () => {
        await this.addToCart();
      });
    }
  }

  _toggleHeader(): void {
    const header = document.querySelector('header.section-header') as HTMLElement | null;
    if (header) {
      header.classList.toggle('-tw-translate-y-36');
    }
    
    const announcement = document.querySelector('announcement-bar') as HTMLElement | null;
    if (announcement) {
      announcement.classList.toggle('-tw-translate-y-full');
    }

    const html = document.documentElement;
    if (html.style.overflow === 'hidden') {
      html.style.overflow = 'visible';
    } else {
      html.style.overflow = 'hidden';
    }
  }

  _openDrawer(): void {
    if (this.bundleDrawer) {
      // Set overflow hidden on both body and html
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';

      // Show the drawer overlay
      this.bundleDrawer.classList.remove('tw-opacity-0', 'tw-pointer-events-none', 'tw-invisible');
      this.bundleDrawer.classList.add('tw-opacity-100', 'tw-pointer-events-auto', 'tw-visible');

      // Slide up the drawer content
      const drawerContent = this.bundleDrawer.querySelector('.js-drawer-content') as HTMLElement | null;
      if (drawerContent) {
        // Use setTimeout to ensure the transition is visible
        setTimeout(() => {
          drawerContent.classList.remove('tw-translate-y-full');
          drawerContent.classList.add('tw-translate-y-0');
        }, 10);
      }

      // Hide header
      const header = document.querySelector('header.section-header') as HTMLElement | null;
      if (header) {
        header.classList.add('-tw-translate-y-36');
      }
      
      const announcement = document.querySelector('announcement-bar') as HTMLElement | null;
      if (announcement) {
        announcement.classList.add('-tw-translate-y-full');
      }
    } else {
      console.warn('Bundle drawer element not found');
    }
  }

  _closeDrawer(): void {
    if (this.bundleDrawer) {
      // Slide down the drawer content first
      const drawerContent = this.bundleDrawer.querySelector('.js-drawer-content') as HTMLElement | null;
      if (drawerContent) {
        drawerContent.classList.remove('tw-translate-y-0');
        drawerContent.classList.add('tw-translate-y-full');
      }

      // Hide the drawer overlay after animation
      setTimeout(() => {
        this.bundleDrawer!.classList.remove('--tw-gradient-from-positionopacity-100', 'tw-pointer-events-auto', 'tw-visible');
        this.bundleDrawer!.classList.add('tw-opacity-0', 'tw-pointer-events-none', 'tw-invisible');
        
        // Reset overflow to empty string (not 'visible') so cart drawer can control it
        document.body.style.overflow = '';
        document.documentElement.style.overflow = '';
        
        // Reset header position
        const header = document.querySelector('header.section-header') as HTMLElement | null;
        if (header && header.classList.contains('-tw-translate-y-36')) {
          header.classList.remove('-tw-translate-y-36');
        }
        
        const announcement = document.querySelector('announcement-bar') as HTMLElement | null;
        if (announcement && announcement.classList.contains('-tw-translate-y-full')) {
          announcement.classList.remove('-tw-translate-y-full');
        }
      }, 300); // Match the transition duration
    }
  }

  _setGiftState(state: any): void {
    if (!this.giftCheckboxesEls.length) {
      console.warn("No gift checkboxes found");
      return;
    }

    this.giftCheckboxesEls.forEach((checkbox) => {
      const giftHandle = checkbox.getAttribute("data-gift-handle");
      if (!giftHandle) return;
      const giftData = state.gifts[giftHandle];

      if (giftData && typeof giftData.gift_quantity === "number") {
        (checkbox as HTMLInputElement).checked = giftData.gift_quantity > 0;
      }
    });
  }

  _initGiftStateAndListener(state: any): void {
    if (this.giftCheckboxesEls.length == 0) return;

    this.giftCheckboxesEls.forEach((el) => {
      const giftHandle = el.getAttribute("data-gift-handle");
      if (!giftHandle || !state.gifts[giftHandle]) return;
      this.updateGiftCheckedState(state.gifts[giftHandle].gift_quantity, giftHandle);

      el.addEventListener("change", (event) => {
        const target = event.target as HTMLInputElement;
        const giftQnt = target.checked ? 1 : 0;
        const giftHandle = target.getAttribute("data-gift-handle");
        if (giftHandle) {
          this.updateGiftCheckedState(giftQnt, giftHandle);
        }
      });
    });
  }

  _isStickySummaryBar(): boolean {
    return this.classList.contains("js-bottom-bar");
  }

  _showStickyOnIntersecting(): void {
    const mmSectionEl = document.querySelector("mix-and-match") as HTMLElement | null;
    const stickyMMBar = this;
    if (!mmSectionEl || !this._isStickySummaryBar()) return;

    const options = {
      rootMargin: `0px 0px -${mmSectionEl.offsetTop}px 0px`,
    };

    let prevZIndex = '';

    const callbackAtcBtn = (entries: IntersectionObserverEntry[]) => {
      const shopifyChat = document.getElementById('ShopifyChat') as HTMLElement | null;
      if (!prevZIndex) {
        prevZIndex = shopifyChat?.style.getPropertyValue('z-index') || '';
      }

      entries.forEach((entrie) => {
        if (window.innerWidth < 1024) {
          if (entrie.isIntersecting) {
            stickyMMBar.classList.add("active");
            if (shopifyChat && prevZIndex) {
              shopifyChat.style.setProperty('z-index', '-1');
            }
          } else {
            stickyMMBar.classList.remove("active");
            if (shopifyChat && prevZIndex) {
              shopifyChat.style.setProperty('z-index', prevZIndex);
              prevZIndex = '';
            }
          }
        }
      });
    };

    const observerMMSection = new IntersectionObserver(callbackAtcBtn, options);

    if (mmSectionEl) {
      observerMMSection.observe(mmSectionEl);
    }
  }

  _updateBottomCards(): void {
    if (!this._isStickySummaryBar()) {
      return;
    }
    const bottomProductCardEls = this.querySelectorAll("mm-product-card");

    this.querySelectorAll(".js-bottom-empty-product").forEach((el, i) => {
      if (i < bottomProductCardEls.length) {
        el.classList.add("tw-hidden");
      }
      else {
        el.classList.remove("tw-hidden");
      }
    });
  }

  _addProductMiniCards(state: any): void {
    let productMiniCards = "";
    const asideProductMiniCardTmpEl = this.querySelector("#template-aside-mm-card")?.cloneNode(true) as HTMLElement | null;
    if (!asideProductMiniCardTmpEl) {
      console.warn("Aside card not found");
      return;
    }
    Array.from(Object.values(state.variants) as any[]).filter((variant: any) => variant.quantity != 0).forEach((variant: any) => {
      if (!this.querySelector(`product-mm-list [data-mix-product="${variant.id}"]`)) {
        let asideProductMiniCard = asideProductMiniCardTmpEl.innerHTML;
        const variantTitle = this._getVariantDisplayName(variant);
        const scriptTagEl = document.querySelector(`[data-product-variants-script-id="${variant.product_type}"]`) as HTMLElement | null;
        // Get image from variant or from product card element
        let variantImage = variant.image;
        if (!variantImage) {
          const productCardEl = document.querySelector(`mm-product-card[data-mix-product="${variant.id}"]`) as HTMLElement | null;
          variantImage = productCardEl?.dataset?.image || "";
          console.warn(`Image missing for variant ${variant.id}, using fallback:`, variantImage);
        }
        
        asideProductMiniCard = asideProductMiniCard.replaceAll("[script_tag]", scriptTagEl?.outerHTML || "");
        asideProductMiniCard = asideProductMiniCard.replaceAll("[variant_id]", String(variant.id));
        asideProductMiniCard = asideProductMiniCard.replaceAll("[variant_category]", variant.category);
        asideProductMiniCard = asideProductMiniCard.replaceAll("[variant_handle]", variant.handle || "");
        asideProductMiniCard = asideProductMiniCard.replaceAll("[variant_price_raw]", String(variant.price));
        asideProductMiniCard = asideProductMiniCard.replaceAll("[variant_original_price]", window.formatMoney(variant.price, window.moneyFormat));
        asideProductMiniCard = asideProductMiniCard.replaceAll("[variant_title]", variantTitle);
        asideProductMiniCard = asideProductMiniCard.replaceAll("[variant_type]", variant.product_type || "");
        asideProductMiniCard = asideProductMiniCard.replaceAll("[variant_image]", variantImage);
        asideProductMiniCard = asideProductMiniCard.replaceAll("[variant_current_value]", String(variant.quantity));
        asideProductMiniCard = asideProductMiniCard.replaceAll("[variant_max_available]", String(variant.inventory_quantity));
        
        // Only show personalization trigger if variant actually has engraving data
        const hasEngraving = variant.propertiesList && variant.propertiesList.length > 0;
        asideProductMiniCard = asideProductMiniCard.replaceAll("[has_engraving_class]", hasEngraving ? "" : "tw-hidden");
        productMiniCards += asideProductMiniCard;
      }
    });

    const targetEl = this.querySelector('product-mm-list');
    if (targetEl && productMiniCards) {
      targetEl.insertAdjacentHTML("beforeend", productMiniCards);
    }
  }

  updateSidebarProductQuantity(variantId: string | number, category: string, handle: string, quantityChange: number): void {
    if (!window.mixMatchStore) return;

    const state = window.mixMatchStore.state;
    const variantKey = `${category}_${variantId}`;
    const variant = state.variants[variantKey];

    if (!variant) {
      console.warn("Variant not found in state:", variantKey);
      return;
    }

    // Don't allow going below 0
    if (variant.quantity + quantityChange < 0) {
      return;
    }

    window.mixMatchStore.setStorageData({
      id: variantId,
      category: category,
      quantity: quantityChange,
      title: variant.title,
      image: variant.image,
      price: variant.price,
      type: variant.type || "product",
      handle: handle,
      inventory_quantity: variant.inventory_quantity,
      product_type: variant.product_type || "",
    });
  }

  getProductHandleFromVariant(variantId: string | number): string {
    // Try to find the handle from existing product cards
    const productCardEls = document.querySelectorAll(
      'mm-product-card[data-mix-product="' + variantId + '"]'
    );
    for (const card of productCardEls) {
      const handle = (card as HTMLElement).dataset.handle;
      if (handle) {
        return handle;
      }
    }

    // Fallback: try to find in state
    if (window.mixMatchStore) {
      const state = window.mixMatchStore.state;
      for (const [key, variant] of Object.entries(state.variants)) {
        if (key.includes(`_${variantId}`) && variant.handle) {
          return variant.handle;
        }
      }
    }

    return "";
  }

  _getVariantDisplayName(variant: any): string {
    // Try to get variant options from the product cards
    const productCardEls = document.querySelectorAll(
      `mm-product-card[data-handle="${variant.handle}"]`
    );

    for (const card of productCardEls) {
      try {
        const variantsData = (card as HTMLElement).dataset.variants;
        const availableVariants = JSON.parse(variantsData || "[]");
        const variantData = availableVariants.find((v: any) => v.id == variant.id);

        if (
          variantData &&
          variantData.options &&
          variantData.options.length > 1
        ) {
          // Show product title + variant options (e.g., "Product Name - Size M, Color Red")
          const options = variantData.options
            .filter((option: string) => option !== "Default Title")
            .join(", ");
          return options ? `${variant.title} - ${options}` : variant.title;
        }
      } catch (e) {
        console.warn("Error parsing variant data:", e);
      }
    }

    // Fallback to just the variant title
    return variant.title;
  }

  _getGiftUnlockThreshold(state: any, giftHandle: string | null): number | undefined {
    if (!giftHandle) return undefined;
    return state.gifts[giftHandle]?.goal_quantity;
  }

  _updateGiftUI(state: any): void {
    const totalQuantity = state.totalQuantity;
    const giftBlockEls = this.querySelectorAll(".js-gift-block");

    if (giftBlockEls.length == 0) {
      console.warn("No gift blocks found");
      return;
    }

    giftBlockEls.forEach((el) => {
      const giftHandle = el.getAttribute("data-gift-handle");
      const unlockThreshold = this._getGiftUnlockThreshold(state, giftHandle);

      if (unlockThreshold === undefined || isNaN(unlockThreshold)) {
        console.warn("Goal quantity is not a number");
        return;
      }

      if (totalQuantity >= unlockThreshold) {
        this.querySelectorAll(".js-gift-empty:not(.tw-hidden)").forEach(el => {
          el.classList.add("tw-hidden");
        });
        this.querySelectorAll(".js-gift-full.tw-hidden").forEach(el => {
          el.classList.remove("tw-hidden");
        });
      }
      else {
        this.querySelectorAll(".js-gift-empty.tw-hidden").forEach(el => {
          el.classList.remove("tw-hidden");
        });
        this.querySelectorAll(".js-gift-full:not(.tw-hidden)").forEach(el => {
          el.classList.add("tw-hidden");
        });
      }
    });
  }

  updateGiftCheckedState(value: number, giftHandle: string): void {
    window.mixMatchStore.updateGiftCheckedState({
      gift_qnt: value,
      gift_handle: giftHandle
    });
  }

  _updateTotalQuantity(state: any): void {
    if (this.totalQuantityEls.length > 0) {
      this.totalQuantityEls.forEach((el) => {
        el.textContent = String(state.totalQuantity);
        el.setAttribute("data-total-quantity", String(state.totalQuantity));
      });
    }
  }

  _updateTotalPrices(state: any): void {
    if (this.totalPriceEl) {
      if (state.totalPrice == 0) {
        this.totalPriceEl.textContent = "";
      }
      else {
        this.totalPriceEl.textContent = window.formatMoney(state.totalPrice, window.moneyFormat);
      }
    }
    else {
      console.warn("Total price element not found");
    }
    if (this.totalPriceWithoutDiscountEl) {
      if (state.totalPriceWithoutDiscount == 0 || state.totalPriceWithoutDiscount == state.totalPrice) {
        this.totalPriceWithoutDiscountEl.textContent = "";
      }
      else {
        this.totalPriceWithoutDiscountEl.textContent = window.formatMoney(
          state.totalPriceWithoutDiscount,
          window.moneyFormat
        );
      }
    }
    else {
      console.warn("Total price without discount element not found");
    }
  }

  _updateButtonsState(state: any): void {
    if (this.addToCartButtonEl) {
      if (state.totalQuantity < state.minimumQuantity) {
        this.addToCartButtonEl.classList.add("disabled");
      }
      else {
        this.addToCartButtonEl.classList.remove("disabled");
      }
    }

    // Update validate bundle button empty state
    if (this.validateBundleBtn) {
      if (state.totalQuantity > 0) {
        this.validateBundleBtn.classList.remove("empty");
      } else {
        this.validateBundleBtn.classList.add("empty");
      }
    }
  }

  _updateMessages(state: any): void {
    const tierInfo = this._calculateTierInfo(state);
    if (!tierInfo) return;
    
    const { requiredQuantity, futureDiscount, giftUnlockDistance, isSingle, isSingleRemaining, isNone, closeToGift } = tierInfo;
    
    // Check if first goal is reached
    const firstGoal = state.discountTiers && state.discountTiers.length > 0 ? state.discountTiers[1]?.quantity : 0;
    const isBeforeFirstGoal = state.totalQuantity < firstGoal;

    this.querySelectorAll(".js-messages-wrap").forEach(wrapper => {
      this._replaceMessagePlaceholders(wrapper as HTMLElement);
      this._updateMessageNumbers(wrapper as HTMLElement, requiredQuantity, futureDiscount, giftUnlockDistance);
      
      // Use different isSingle logic based on wrapper type
      const isProgressText = wrapper.classList.contains('js-progress-text');
      const useSingle = isProgressText ? isSingle : isSingleRemaining;
      
      this._toggleMessageBlocks(wrapper as HTMLElement, giftUnlockDistance, useSingle, closeToGift);
      this._toggleReachedState(wrapper as HTMLElement, isNone, isBeforeFirstGoal);
    });
  }

  _calculateTierInfo(state: any): TierInfo | null {
    const currentDiscount = state.currentDiscount;
    const currentQuantity = state.totalQuantity;
    if (isNaN(currentDiscount) || isNaN(currentQuantity)) return null;

    const { discountTiers } = state;
    const higherDiscountIndex = discountTiers.findIndex((item: any) => item.discount > currentDiscount);
    const exactDiscountIndex = discountTiers.findIndex((item: any) => item.discount == currentDiscount);
    const tierIndex = higherDiscountIndex !== -1 ? higherDiscountIndex : exactDiscountIndex;
    if (tierIndex === -1) return null;

    const nextTier = discountTiers[tierIndex];
    const requiredQuantity = Math.max(0, nextTier.quantity);
    const remainingQuantity = Math.max(0, requiredQuantity - currentQuantity);
    const futureDiscount = nextTier.discount;

    // Calculate the difference between current tier and next tier
    // This is the total amount needed to reach next goal from current tier start
    const currentTierIndex = exactDiscountIndex !== -1 ? exactDiscountIndex : (higherDiscountIndex > 0 ? higherDiscountIndex - 1 : 0);
    const currentTier = discountTiers[currentTierIndex];
    const tierDifference = requiredQuantity - (currentTier ? currentTier.quantity : 0);

    const firstGiftBlockEl = this.querySelector(".js-gift-block");
    const firstGiftHandle = firstGiftBlockEl?.getAttribute("data-gift-handle") || "";
    const giftUnlockThreshold = this._getGiftUnlockThreshold(state, firstGiftHandle);
    const giftUnlockDistance = Math.max(0, (giftUnlockThreshold || 0) - currentQuantity);

    return {
      requiredQuantity: tierDifference,
      remainingQuantity,
      futureDiscount,
      nextTier,
      giftUnlockDistance,
      isSingle: tierDifference === 1, // For progress text: tier difference is 1
      isSingleRemaining: remainingQuantity === 1, // For regular messages: 1 product remaining to next goal
      isNone: remainingQuantity === 0,
      closeToGift: giftUnlockDistance < 4
    };
  }

  _replaceMessagePlaceholders(wrapper: HTMLElement): void {
    wrapper.innerHTML = wrapper.innerHTML
      .replaceAll("[discount]", '<span class="js-closest-discount"></span>')
      .replaceAll("[number]", '<span class="js-closest-product"></span>')
      .replaceAll("[gift_number]", '<span class="js-gift-number"></span>')
      .replaceAll("[next_product]", '<span class="js-next-product-number"></span>');
  }

  _updateMessageNumbers(wrapper: HTMLElement, requiredQuantity: number, futureDiscount: number, giftUnlockDistance: number): void {
    const state = window.mixMatchStore.state;
    const currentQuantity = state.totalQuantity || 0;
    
    // Calculate next product number: current + remaining to next goal
    const remainingToNextGoal = this._calculateRemainingToNextGoal(state);
    const nextProductNumber = remainingToNextGoal > 0 ? currentQuantity + remainingToNextGoal : currentQuantity + 1;
    
    // Check if this is the progress text wrapper (should show tier difference)
    // or regular message wrapper (should show total: current + remaining)
    const isProgressText = wrapper.classList.contains('js-progress-text');
    const isMobile = window.innerWidth < 768;
    
    wrapper.querySelectorAll(".js-closest-product").forEach(el => {
      if (isProgressText) {
        // Progress bar text:
        // Desktop: show tier difference (constant until goal reached)
        // Mobile: show actual remaining (decreases as you add products)
        el.textContent = String(isMobile ? remainingToNextGoal : requiredQuantity);
      } else {
        // Regular messages: show next product number (current + remaining)
        el.textContent = String(nextProductNumber);
      }
    });
    wrapper.querySelectorAll(".js-closest-discount").forEach(el => {
      el.textContent = String(futureDiscount);
    });
    wrapper.querySelectorAll(".js-gift-number").forEach(el => {
      el.textContent = String(giftUnlockDistance);
    });
    wrapper.querySelectorAll(".js-next-product-number").forEach(el => {
      el.textContent = this._formatOrdinalNumber(nextProductNumber);
    });
  }
  
  _calculateRemainingToNextGoal(state: any): number {
    const currentDiscount = state.currentDiscount;
    const currentQuantity = state.totalQuantity || 0;
    if (isNaN(currentDiscount) || isNaN(currentQuantity)) return 0;

    const { discountTiers } = state;
    const higherDiscountIndex = discountTiers.findIndex((item: any) => item.discount > currentDiscount);
    const exactDiscountIndex = discountTiers.findIndex((item: any) => item.discount == currentDiscount);
    const tierIndex = higherDiscountIndex !== -1 ? higherDiscountIndex : exactDiscountIndex;
    if (tierIndex === -1) return 0;

    const nextTier = discountTiers[tierIndex];
    const requiredQuantity = Math.max(0, nextTier.quantity);
    const remainingQuantity = Math.max(0, requiredQuantity - currentQuantity);
    
    return remainingQuantity;
  }
  
  _formatOrdinalNumber(num: number): string {
    if (num === 1) return '1er';
    return `${num}e`;
  }

  _toggleMessageBlocks(wrapper: HTMLElement, giftUnlockDistance: number, isSingle: boolean, closeToGift: boolean): void {
    const messageElements = this._getMessageElements(wrapper);

    if (this._shouldShowGiftPromotion(wrapper, closeToGift, messageElements)) {
      this._showGiftPromotionMessage(messageElements);
    } else if (this._shouldShowProgressMessage(wrapper, closeToGift, giftUnlockDistance, messageElements)) {
      this._showProgressMessage(messageElements, giftUnlockDistance);
    } else {
      this._showDefaultMessage(messageElements, isSingle);
    }
  }

  _getMessageElements(wrapper: HTMLElement): MessageElements {
    return {
      singleTextEl: wrapper.querySelector(".js-single-product-text"),
      multipleTextEl: wrapper.querySelector(".js-multiple-product-text"),
      singleAddAndGetTextEl: wrapper.querySelector(".js-single-add-and-get"),
      multipleAddAndGetTextEl: wrapper.querySelector(".js-multiple-add-and-get")
    };
  }

  _shouldShowGiftPromotion(wrapper: HTMLElement, closeToGift: boolean, messageElements: MessageElements): boolean {
    return wrapper.classList.contains("js-gift-text") &&
      !closeToGift &&
      messageElements.multipleAddAndGetTextEl !== null;
  }

  _shouldShowProgressMessage(wrapper: HTMLElement, closeToGift: boolean, giftUnlockDistance: number, messageElements: MessageElements): boolean {
    return wrapper.classList.contains("js-progress-text") &&
      closeToGift &&
      giftUnlockDistance > 0 &&
      messageElements.multipleAddAndGetTextEl !== null &&
      messageElements.singleAddAndGetTextEl !== null;
  }

  _showGiftPromotionMessage(messageElements: MessageElements): void {
    messageElements.multipleAddAndGetTextEl?.classList.remove("tw-hidden");
  }

  _showProgressMessage(messageElements: MessageElements, giftUnlockDistance: number): void {
    const isGiftSingle = giftUnlockDistance === 1;
    messageElements.singleAddAndGetTextEl?.classList.toggle("tw-hidden", !isGiftSingle);
    messageElements.multipleAddAndGetTextEl?.classList.toggle("tw-hidden", isGiftSingle);
    messageElements.singleTextEl?.classList.add("tw-hidden");
    messageElements.multipleTextEl?.classList.add("tw-hidden");
  }

  _showDefaultMessage(messageElements: MessageElements, isSingle: boolean): void {
    messageElements.singleTextEl?.classList.toggle("tw-hidden", !isSingle);
    messageElements.multipleTextEl?.classList.toggle("tw-hidden", isSingle);
    messageElements.singleAddAndGetTextEl?.classList.add("tw-hidden");
    messageElements.multipleAddAndGetTextEl?.classList.add("tw-hidden");
  }

  _toggleReachedState(wrapper: HTMLElement, isNone: boolean, isBeforeFirstGoal: boolean): void {
    const reachedTextEl = wrapper.querySelector(".js-riched-max");
    const progressWrapperEl = wrapper.querySelector(".js-progress-wrapper");
    const startTextEl = wrapper.querySelector(".js-start-text");
    const discountTextEl = wrapper.querySelector(".js-discount-text");
    const messagesWrapEl = this.querySelector(".js-messages-wrap")?.parentElement;
    const wrapParentEl = wrapper.closest(".js-messages-wrap:not(.js-progress-text)")?.parentElement;
    
    const isMobile = window.innerWidth < 768;
    const state = window.mixMatchStore.state;
    const hasAddedProducts = state.totalQuantity > 0;
    
    if (isNone) {
      // Max tier reached - show success message
      this.querySelectorAll(".js-single-product-text, .js-multiple-product-text")
        .forEach(el => el.classList.add("tw-hidden"));
      progressWrapperEl?.classList.add("tw-hidden");
      reachedTextEl?.classList.remove("tw-hidden");
      wrapParentEl?.classList.add("tw-hidden");
    } else if (isBeforeFirstGoal && !(isMobile && hasAddedProducts)) {
      // Before first goal - show start text
      // On mobile: hide start text once first product is added
      progressWrapperEl?.classList.remove("tw-hidden");
      startTextEl?.classList.remove("tw-hidden");
      discountTextEl?.classList.add("tw-hidden");
      messagesWrapEl?.classList.remove("tw-hidden");
      reachedTextEl?.classList.add("tw-hidden");
      wrapParentEl?.classList.remove("tw-hidden");
    } else {
      // First goal reached (or mobile with products added) - show discount text
      progressWrapperEl?.classList.remove("tw-hidden");
      startTextEl?.classList.add("tw-hidden");
      discountTextEl?.classList.remove("tw-hidden");
      messagesWrapEl?.classList.remove("tw-hidden");
      reachedTextEl?.classList.add("tw-hidden");
      wrapParentEl?.classList.remove("tw-hidden");
    }
  }

  _updateGoalImages(state: any): void {
    const currentQuantity = state.totalQuantity || 0;
    const goalContainers = this.querySelectorAll(".js-goal");

    if (goalContainers.length === 0) return;

    // Get thresholds from discount tiers
    const thresholds = state.discountTiers && state.discountTiers.length > 0
      ? state.discountTiers.map((tier: any) => tier.quantity)
      : [0, 2, 3, 4, 5, 6];

    // Skip first threshold (0) for image progression
    const goalThresholds = thresholds[0] === 0 ? thresholds.slice(1) : thresholds;

    let activeImageIndex = 0;

    if (currentQuantity > 0 && goalThresholds.length > 0) {
      // Find which goal to show
      for (let i = 0; i < goalThresholds.length; i++) {
        if (currentQuantity < goalThresholds[i]) {
          activeImageIndex = i;
          break;
        } else if (i === goalThresholds.length - 1) {
          // Reached or exceeded the last goal
          activeImageIndex = i;
        }
      }
    }

    // Ensure index doesn't exceed available goals
    const displayIndex = Math.min(activeImageIndex, goalContainers.length - 1);

    // Show only the active goal, hide others
    goalContainers.forEach((container, index) => {
      container.classList.toggle("tw-hidden", index !== displayIndex);
    });
  }

  _updateProgressBar(state: any): void {
    if (this.progressEls.length === 0) {
      console.warn("Progress bar element not found");
      return;
    }

    if (!this._isValidProgressState(state)) {
      console.warn("Cannot update progress bar. Check current discount or quantity");
      return;
    }

    this.progressEls.forEach(progressBarEl => {
      this._updateSingleProgressBar(progressBarEl as HTMLElement, state);
    });
  }

  _isValidProgressState(state: any): boolean {
    return !isNaN(state.currentDiscount) && !isNaN(state.totalQuantity);
  }

  _updateSingleProgressBar(progressBarEl: HTMLElement, state: any): void {
    const progressElements = this._getProgressElements(progressBarEl);
    const tierInfo = this._calculateTierInfo(state);

    if (!tierInfo?.nextTier) return;

    this._updateDiscountProgressItems(
      progressElements.discountItems,
      tierInfo.requiredQuantity,
      tierInfo.remainingQuantity,
      state.currentDiscount,
      tierInfo.futureDiscount
    );

    this._updateGiftProgressItems(progressElements.giftItems, state);
  }

  _getProgressElements(progressBarEl: HTMLElement): ProgressElements {
    return {
      discountItems: progressBarEl.querySelectorAll('.js-bundle-progress-item'),
      giftItems: progressBarEl.querySelectorAll('.js-bundle-gift-progress-item')
    };
  }

  _updateDiscountProgressItems(progressItemEls: NodeListOf<Element>, requiredQuantity: number, remainingQuantity: number, currentDiscount: number, futureDiscount: number): void {
    const state = window.mixMatchStore.state;
    const currentQuantity = state.totalQuantity || 0;
    const discountTiers = state.discountTiers || [];

    progressItemEls.forEach((item) => {
      const itemDiscount = parseFloat((item as HTMLElement).dataset.discount || '0');

      if (isNaN(itemDiscount)) {
        return;
      }

      // Find the tier that corresponds to this progress item
      const tierIndex = discountTiers.findIndex((tier: any) => tier.discount === itemDiscount);
      if (tierIndex === -1) {
        (item as HTMLElement).style.setProperty("--discountProgress", "0%");
        (item as HTMLElement).dataset.progress = "0%";
        return;
      }

      const currentTier = discountTiers[tierIndex];
      const previousTier = tierIndex > 0 ? discountTiers[tierIndex - 1] : { quantity: 0 };

      const tierStartQuantity = previousTier.quantity;
      const tierEndQuantity = currentTier.quantity;
      const tierRange = tierEndQuantity - tierStartQuantity;

      let discountProgress: string;

      if (currentQuantity >= tierEndQuantity) {
        // Tier is fully completed
        discountProgress = "100%";
      } else if (currentQuantity > tierStartQuantity) {
        // In progress within this tier
        const progressInTier = currentQuantity - tierStartQuantity;
        const progressPercentage = (progressInTier / tierRange) * 100;
        discountProgress = `${Math.min(100, Math.max(0, progressPercentage))}%`;
      } else {
        // Haven't reached this tier yet
        discountProgress = "0%";
      }

      (item as HTMLElement).style.setProperty("--discountProgress", discountProgress);
      (item as HTMLElement).dataset.progress = discountProgress;
    });
  }

  _updateGiftProgressItems(giftProgressItemEls: NodeListOf<Element>, state: any): void {
    const totalQuantity = state.totalQuantity;
    if (giftProgressItemEls.length > 0) {
      giftProgressItemEls.forEach(item => {
        const giftHandle = item.getAttribute("data-gift-handle");
        const unlockThreshold = this._getGiftUnlockThreshold(state, giftHandle);
        if (unlockThreshold === undefined || isNaN(unlockThreshold)) {
          console.warn("Goal quantity is not a number");
          return;
        }
        const qntDiff = totalQuantity - unlockThreshold;
        let giftProgress = "0%";

        if (qntDiff >= 0) {
          giftProgress = "100%";
        } else if (qntDiff > -4) {
          // Close to unlocking: 1 away = 75%, 2 away = 50%, 3 away = 25%
          giftProgress = `${(4 + qntDiff) * 25}%`;
        }

        (item as HTMLElement).style.setProperty('--discountProgress', giftProgress);
        (item as HTMLElement).dataset.progress = giftProgress;
      });
    } else {
      console.warn("Gift progress bar element not found");
    }
  }

  static async _removeBundleFromCart(key: string): Promise<void> {
    const updates: { [key: string]: number } = {};
    updates[key] = 0;

    const response = await fetch(
      window.Shopify.routes.root + "cart/update.js",
      {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          updates: updates,
        }),
      }
    );

    if (!response.ok) {
      console.error("Error removing bundle from cart");
    }
  }

  async addToCart(): Promise<void> {
    // Find the appropriate add to cart button (regular or drawer)
    const regularAddBtn = this.addToCartButtonEl;
    const drawerAddBtn = this.querySelector(".js-drawer-add-to-cart") as HTMLElement | null;
    const activeAddBtn = drawerAddBtn && this.bundleDrawer && this.bundleDrawer.classList.contains('tw-visible')
      ? drawerAddBtn
      : regularAddBtn;

    if (!activeAddBtn) {
      console.error('No add to cart button found');
      return;
    }

    activeAddBtn.setAttribute("aria-busy", "true");
    const state = window.mixMatchStore.state;

    // Close drawer immediately if it's open
    const isDrawerOpen = this.bundleDrawer && this.bundleDrawer.classList.contains('tw-visible');
    if (isDrawerOpen) {
      this._closeDrawer();
    }

    try {
      await this._removePreviousBundleIfExists();
      const bundleData = this._prepareBundleData(state);
      const cartFormData = this._createCartFormData(bundleData, state);
      await this._submitToCart(cartFormData);
      
      // Add box products as separate line items
      await this._addBoxProductsToCart(state);
      
      await this._handleSuccessfulCartAdd();
    } catch (error) {
      console.error('Add to cart error:', error);
      activeAddBtn.setAttribute("aria-busy", "false");
      throw error;
    }
  }

  async _removePreviousBundleIfExists(): Promise<void> {
    const bundleKeyToRemove = this.addToCartButtonEl?.getAttribute("data-mm-add-to-cart");
    if (bundleKeyToRemove != "" && bundleKeyToRemove != null) {
      await MMCartSummary._removeBundleFromCart(bundleKeyToRemove);
    }
  }

  _prepareBundleData(state: any): BundleLineItem[] {
    const bundleLineItems = this._collectBundleLineItems(state);
    const eligibleGifts = this._collectEligibleGifts(state);
    // Box products are NOT included in bundle data - they're added separately
    return [...bundleLineItems, ...eligibleGifts];
  }

  _collectBundleLineItems(state: any): BundleLineItem[] {
    const lineItems: BundleLineItem[] = [];

    for (const variant of Object.values(state.variants) as any[]) {
      if (variant.quantity === 0) continue;

      // Check if variant has engraving properties in the list
      const propertiesList = variant.propertiesList || [];
      const hasEngravingProps = propertiesList.length > 0;

      if (hasEngravingProps) {
        // Create separate line items for each properties entry
        for (let i = 0; i < propertiesList.length; i++) {
          const engravingProps = this._transformEngravingProperties(propertiesList[i]);
          
          lineItems.push({
            variantId: variant.id,
            quantity: 1,
            category: variant.category || "",
            type: variant.type,
            price: variant.price,
            properties: {
              _handle: variant.handle,
              ...engravingProps
            }
          });
        }
        
        // If there are more quantity than properties entries, add remaining without engraving
        const remainingQty = variant.quantity - propertiesList.length;
        if (remainingQty > 0) {
          lineItems.push({
            variantId: variant.id,
            quantity: remainingQty,
            category: variant.category || "",
            type: variant.type,
            price: variant.price,
            properties: {
              _handle: variant.handle
            }
          });
        }
      } else {
        // No engraving - add as single line item with full quantity
        lineItems.push({
        variantId: variant.id,
        quantity: variant.quantity,
        category: variant.category || "",
        type: variant.type,
          price: variant.price,
        properties: {
          _handle: variant.handle
        }
        });
      }
    }

    return lineItems;
  }

  _transformEngravingProperties(properties: Record<string, string>): Record<string, string> {
    const transformed: Record<string, string> = {};

    for (const [key, value] of Object.entries(properties)) {
      if (!value || key.startsWith('_')) continue; // Skip internal props like _handle

      const isEngravingCartFormat = key.match(/^Face\s+\d+\s*-\s*Gravure\s+\d+$/i);
      const isFontCartFormat = key.match(/^Typographie\s+face\s+\d+$/i);
      if (isEngravingCartFormat || isFontCartFormat) {
        transformed[key] = value;
        continue;
      }
      
      // Check for engraving pattern: face1-engraving1, face1-engraving2
      const engravingMatch = key.match(/face(\d+)-engraving(\d+)/);
      if (engravingMatch) {
        const faceNum = engravingMatch[1];
        const gravureNum = engravingMatch[2];
        transformed[`Face ${faceNum} - Gravure ${gravureNum}`] = value;
        continue;
      }

      // Check for fontStyle pattern: face1-fontStyle
      const fontMatch = key.match(/face(\d+)-fontStyle/);
      if (fontMatch) {
        const faceNum = fontMatch[1];
        transformed[`Typographie face ${faceNum}`] = value;
        continue;
      }
    }

    return transformed;
  }

  async _addBoxProductsToCart(state: any): Promise<void> {
    // Add box products as separate line items (not part of the bundle)
    const boxProducts = Object.values(state.boxProducts)
      .filter((boxProduct: any) => boxProduct.quantity > 0);

    if (boxProducts.length === 0) {
      return;
    }

    for (const boxProduct of boxProducts as any[]) {
      try {
        const boxProductFormData = {
          items: [{
            id: parseInt(String(boxProduct.id)),
            quantity: boxProduct.quantity,
            properties: {
              _box_product: true,
              _handle: boxProduct.handle
            }
          }]
        };

        const response = await fetch("/cart/add.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(boxProductFormData)
        });

        if (!response.ok) {
          const json = await response.json();
          console.error(`Failed to add box product ${boxProduct.title}:`, json);
        } else {
          console.log(`Successfully added box product ${boxProduct.title}`);
        }
      } catch (error) {
        console.error(`Error adding box product ${boxProduct.title}:`, error);
      }
    }
    
    // Update cart display after adding box products
    if (typeof update_cart === 'function') {
      update_cart();
    }
  }

  _collectEligibleGifts(state: any): BundleLineItem[] {
    return Object.values(state.gifts)
      .filter((gift: any) => state.totalQuantity >= gift.goal_quantity && gift.gift_quantity > 0)
      .map((gift: any) => ({
        variantId: gift.id,
        quantity: gift.gift_quantity,
        category: "",
        type: gift.type,
        properties: {
          _handle: gift.handle
        }
      }));
  }

  _createCartFormData(bundleLineItems: BundleLineItem[], state: any): CartFormData {
    const identifier = this._generateBundleIdentifier();
    const currentBundleId = this._getCurrentBundleVariantId(state);
    const productList = this._createProductList(bundleLineItems);
    const addonsListString = this._createAddonsString(bundleLineItems, state);
    
    const bundlePriceWithoutDiscount = this._calculateBundleOnlyPrice(state);
    
    const bundleFinalPrice = this._calculateBundleFinalPrice(state);

    return {
      items: [{
        id: currentBundleId,
        quantity: 1,
        properties: {
          "__foxsell:dynamic_add_on_bundle_id": identifier,
          "__foxsell:dynamic_add_on_bundle_items": JSON.stringify(bundleLineItems),
          "_bundle_products": productList,
          "_bundle_addons": addonsListString,
          "_bundle": true,
          "_bundle_price": bundlePriceWithoutDiscount,
          "_bundle_final_price": bundleFinalPrice,
          "_bundle_discount": state.currentDiscount || 0,
          "_bundle_qnt": state.totalQuantity,
          "_bundle_id": identifier,
        }
      }]
    };
  }

  _calculateBundleOnlyPrice(state: any): number {
    let bundlePrice = 0;
    for (const productKey in state.variants) {
      const product = state.variants[productKey];
      bundlePrice += product.price * product.quantity;
    }
    return bundlePrice;
  }

  _calculateBundleFinalPrice(state: any): number {
    // Calculate final price with discount applied per product
    let totalPrice = 0;
    for (const productKey in state.variants) {
      const product = state.variants[productKey];
      totalPrice += Math.ceil(product.price * ((100 - (state.currentDiscount || 0)) / 100)) * product.quantity;
    }
    return totalPrice;
  }

  _generateBundleIdentifier(): string {
    const bundleId = JSON.parse(window.sessionStorage.getItem("mixBundleId") || '""');
    const currentDate = Date.now();
    return `${bundleId}_${currentDate}`;
  }

  _getCurrentBundleVariantId(state: any): string {
    const mixDiscountObject = JSON.parse(window.sessionStorage.getItem("mixDiscount") || '[]');
    const matchingDiscountIndex = mixDiscountObject.findLastIndex(
      (discount: any) => state.totalQuantity >= discount[Object.keys(discount)[0]].quantity
    );

    if (matchingDiscountIndex === -1) {
      throw new Error("Matching discount option not found");
    }

    const variantGid = Object.keys(mixDiscountObject[matchingDiscountIndex])[0];
    return variantGid.split("/").pop() || '';
  }

  _createProductList(bundleLineItems: BundleLineItem[]): string {
    return bundleLineItems
      .map(product => `${product.properties._handle}:${product.variantId}`)
      .join(",");
  }

  _createAddonsString(bundleLineItems: BundleLineItem[], state: any): string {
    const allGiftHandles = Object.keys(state.gifts);
    const addonsList = bundleLineItems.filter(product => product.type === 'addOns');

    const cartGiftMap: { [key: string]: number } = {};
    addonsList.forEach(product => {
      const handle = product.properties._handle;
      const quantity = product.quantity || 0;
      cartGiftMap[handle] = quantity;
    });

    return allGiftHandles
      .map(handle => `${handle}:${cartGiftMap[handle] ?? 0}`)
      .join(',');
  }

  async _submitToCart(cartFormData: CartFormData): Promise<any> {
    const response = await fetch("/cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(cartFormData)
    });

    const json = await response.json();

    if (!response.ok) {
      throw new Error(`${json?.message}: ${json?.description}`);
    }

    // Update cart and dispatch events
    if (typeof update_cart === 'function') {
      update_cart(function () {
        const event = document.createEvent("Event");
        event.initEvent("updated_cart", true, true);
        document.dispatchEvent(event);
        
        const event_atc = document.createEvent("Event");
        event_atc.initEvent("added_to_cart", true, true);
        document.dispatchEvent(event_atc);
      });
    }

    document.body.style.overflow = '';
    document.documentElement.style.overflow = '';
    
    // Open cart panel automatically (outside update_cart callback)
    const $cartBtn = document.querySelector(".header-icon__cart") as HTMLElement | null;
    if ($cartBtn) {
      $cartBtn.click();
    }
    
    // Trigger add-to-cart animation
    if (document.querySelector("[data-function='atc-animation']")) {
      const atc_animation = document.querySelectorAll("[data-function='atc-animation']");
      atc_animation.forEach(function (b) {
        b.classList.add("animation-in");
        setTimeout(function () {
          b.classList.remove("animation-in");
        }, 3000);
      });
    }

    return json;
  }

  async _handleSuccessfulCartAdd(): Promise<void> {
    await window.fetchAndCacheCart();

    window.mixMatchStore.clearState();
    window.mixMatchStore.updateStorageTotals();
    window.mixMatchStore.persist();
    window.mixMatchStore.notify("UPDATE_VARIANT_QUANTITY");
    window.mixMatchStore.notify("UPDATE_BOX_PRODUCT_QUANTITY");
    window.mixMatchStore.notify("GIFT_CHECKED_STATE_UPDATED");

    if (this.addToCartButtonEl) {
      this.addToCartButtonEl.setAttribute("aria-busy", "false");
      this.addToCartButtonEl.setAttribute("data-mm-add-to-cart", "");
    }
  }

  onStateChange({ action, previousState, state }: { action: string; previousState: any; state: any }): void {
    if (action === "UPDATE_VARIANT_QUANTITY") {
      this._addProductMiniCards(state);
      this._updateGiftUI(state);
      this._updateTotalQuantity(state);
      this._updateTotalPrices(state);
      this._updateButtonsState(state);
      this._updateProgressBar(state);
      this._updateMessages(state);
      this._updateBottomCards();
      this._updateGoalImages(state);
      this._updateEngravingTriggers(state);
    }

    if (action === "GIFT_CHECKED_STATE_UPDATED") {
      this._setGiftState(state);
    }
  }

  _updateEngravingTriggers(state: any): void {
    // Update visibility of engraving triggers based on propertiesList ONLY
    // Only show trigger if the variant actually has engraving data
    const engravingTriggers = this.querySelectorAll(".js-engraving-info-trigger");
    engravingTriggers.forEach((trigger) => {
      // Use the card's data-mix-product (correct variant ID) instead of trigger's data-variant-id
      const cardEl = trigger.closest('mm-product-card') as HTMLElement;
      const cardVariantId = cardEl?.dataset?.mixProduct;
      if (!cardVariantId) return;

      // Update trigger's data-variant-id to match the card's variant ID
      (trigger as HTMLElement).dataset.variantId = cardVariantId;

      // Find the variant in state and check if it has engraving data
      let hasEngraving = false;
      
      for (const key of Object.keys(state.variants)) {
        if (key.endsWith(`_${cardVariantId}`) || key === `_${cardVariantId}`) {
          const variant = state.variants[key];
          if (variant?.propertiesList && variant.propertiesList.length > 0) {
            hasEngraving = true;
            break;
          }
        }
      }
      
      if (hasEngraving) {
        trigger.classList.remove("tw-hidden");
      } else {
        trigger.classList.add("tw-hidden");
      }
    });
  }
}

